import { err, ok } from '@shared/utils';
import type { AppError, AsyncResult } from '@shared/utils';
import type { PackageRegistryEntry } from '../../config/interfaces/PackageRegistryEntry';
import type { BootstrapRunReport } from '../interfaces/BootstrapRunReport';
import type { PackageRuntimeBootstrapReport } from '../interfaces/PackageRuntimeBootstrapReport';
import { BOOTSTRAP_ERROR_CODES, BOOTSTRAP_MESSAGES } from '../constants';

type Registries = Record<string, PackageRegistryEntry<any>>;

let hasRun = false;
let runOncePromise: AsyncResult<BootstrapRunReport, AppError> | null = null;
let lastRunReport: BootstrapRunReport | null = null;

/**
 * @group Bootstrap
 * @summary
 * <blockquote>Orchestrates the bootstrap process for all registered packages, running each
 * package's `configureRuntime` and post-bootstrap tasks in registration order.</blockquote>
 *
 * @remarks
 * Guarantees at-most-once execution: concurrent callers share the same in-flight
 * promise and subsequent calls return the cached report.
 *
 * @example
 * ```typescript
 * import { BootstrapRunner } from '@shared/core';
 *
 * const result = await BootstrapRunner.run(registries);
 * result.match({
 *   ok: (report) => console.log('Bootstrap succeeded', report.status),
 *   err: (error) => console.error('Bootstrap failed', error.errorMessage),
 * });
 * ```
 */
export class BootstrapRunner {
  static async run(registries: Registries): AsyncResult<BootstrapRunReport, AppError> {
    if (hasRun && lastRunReport) {
      return ok(lastRunReport);
    }

    if (runOncePromise) {
      return runOncePromise;
    }

    runOncePromise = BootstrapRunner.execute(registries);

    const result = await runOncePromise;
    runOncePromise = null;

    if (result.isOk) {
      hasRun = true;
      lastRunReport = result.value;
    }
    return result;
  }

  static runDetached(registries: Registries): void {
    void BootstrapRunner.run(registries);
  }

  private static async execute(registries: Registries): AsyncResult<BootstrapRunReport, AppError> {
    const packageReport: PackageRuntimeBootstrapReport = {
      startedAt: new Date().toISOString(),
      finishedAt: null,
      status: 'ok',
      packageResults: [],
    };

    for (const [key, entry] of Object.entries(registries)) {
      const packageResult: PackageRuntimeBootstrapReport['packageResults'][number] = {
        packageId: entry.definition.packageId,
        status: 'ok',
        steps: [],
      };
      packageReport.packageResults.push(packageResult);

      const activeRuntime = entry.definition.runtimeKind;
      for (const envConfig of Object.values(entry.config)) {
        for (const adapter of envConfig.adapters) {
          if (!adapter.supportedRuntimes.includes(activeRuntime)) {
            packageResult.status = 'err';
            packageResult.steps.push({ id: 'adapter-runtime-validation', status: 'err', error: { errorCode: BOOTSTRAP_ERROR_CODES.BOOTSTRAP_ADAPTER_RUNTIME_MISMATCH, errorMessage: BOOTSTRAP_MESSAGES.BOOTSTRAP_ADAPTER_RUNTIME_MISMATCH } });
            packageReport.status = 'err';
            packageReport.finishedAt = new Date().toISOString();
            return err({
              errorCode: BOOTSTRAP_ERROR_CODES.BOOTSTRAP_ADAPTER_RUNTIME_MISMATCH,
              errorMessage: BOOTSTRAP_MESSAGES.BOOTSTRAP_ADAPTER_RUNTIME_MISMATCH,
              metadata: { packageId: entry.definition.packageId, registryKey: key, activeRuntime, adapterRuntimes: [...adapter.supportedRuntimes], packageReport },
            });
          }
        }
      }
      packageResult.steps.push({ id: 'adapter-runtime-validation', status: 'ok' });

      const configResult = await entry.definition.configureRuntime(entry.config);
      if (configResult.isErr) {
        packageResult.status = 'err';
        packageResult.steps.push({ id: 'configure-runtime', status: 'err', error: configResult.error });
        packageReport.status = 'err';
        packageReport.finishedAt = new Date().toISOString();
        return err({
          errorCode: BOOTSTRAP_ERROR_CODES.PACKAGE_RUNTIME_CONFIG_FAILED,
          errorMessage: BOOTSTRAP_MESSAGES.PACKAGE_RUNTIME_CONFIG_FAILED,
          metadata: { packageId: entry.definition.packageId, registryKey: key, cause: configResult.error, packageReport },
        });
      }
      packageResult.steps.push({ id: 'configure-runtime', status: 'ok' });

      const postTasksResult = entry.definition.getPostBootstrapTasks();
      if (postTasksResult.isErr) {
        packageResult.status = 'err';
        packageResult.steps.push({ id: 'load-post-bootstrap-tasks', status: 'err', error: postTasksResult.error });
        packageReport.status = 'err';
        packageReport.finishedAt = new Date().toISOString();
        return err({
          errorCode: BOOTSTRAP_ERROR_CODES.PACKAGE_RUNTIME_POST_BOOTSTRAP_FAILED,
          errorMessage: BOOTSTRAP_MESSAGES.PACKAGE_RUNTIME_POST_BOOTSTRAP_FAILED,
          metadata: { packageId: entry.definition.packageId, registryKey: key, cause: postTasksResult.error, packageReport },
        });
      }
      packageResult.steps.push({ id: 'load-post-bootstrap-tasks', status: 'ok' });

      for (const task of postTasksResult.value) {
        const taskResult = await task.run();
        if (taskResult.isErr) {
          packageResult.status = 'err';
          packageResult.steps.push({ id: `post-bootstrap:${task.id}`, status: 'err', error: taskResult.error });
          packageReport.status = 'err';
          packageReport.finishedAt = new Date().toISOString();
          return err({
            errorCode: BOOTSTRAP_ERROR_CODES.PACKAGE_RUNTIME_POST_BOOTSTRAP_FAILED,
            errorMessage: BOOTSTRAP_MESSAGES.PACKAGE_RUNTIME_POST_BOOTSTRAP_FAILED,
            metadata: { packageId: entry.definition.packageId, taskId: task.id, cause: taskResult.error, packageReport },
          });
        }
        packageResult.steps.push({ id: `post-bootstrap:${task.id}`, status: 'ok' });
      }
    }

    packageReport.finishedAt = new Date().toISOString();

    const report: BootstrapRunReport = {
      startedAt: packageReport.startedAt,
      finishedAt: packageReport.finishedAt,
      status: 'ok',
      taskResults: [{
        taskId: 'package-runtime-bootstrap',
        status: 'ok',
        packageReport,
      }],
    };

    return ok(report);
  }

  static resetState(): void {
    hasRun = false;
    runOncePromise = null;
    lastRunReport = null;
  }
}
